import { Box, Button, Divider, Typography } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import {
	getActiveTicket,
	getEditMode,
	getIsNotificationLoading,
	getIsTicketNavigationButtonsAvailable,
	getVoiceMode,
} from 'store/ticket-service/selector';
import {
	setActiveStep,
	setIsNotificationLoading,
	setIsTicketUpdate,
	setIsTyping,
	updateLocalTicket,
} from 'store/ticket-service/ticketSlice';
import { INotification } from 'store/ticket-service/types';
import { getChoosedFile } from 'store/user-service/selector';
import { triggerTicketSave } from 'store/user-service/userSlice';

import { useEventBus } from 'utils/EventBus';

import {
	getNotifications,
	getNotificationsConversation,
	getPotentialNotifications,
} from 'core/chains/ChatGptRequests';
import { notEnoughTokensErrorMessage } from 'core/constants/chatGptRequestRegenerateMessages';
import {
	MessageActionStates,
	MessageActionTypes,
	MessageSender,
	NamespacesList,
	TicketStages,
} from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';
import useSpeechToText from 'core/hooks/useSpeechToText';
import useTextToSpeech from 'core/hooks/useTextToSpeech';

import { FormTitleValue } from 'pages/Home';

import { IMessage } from 'components/Chatbot/Message/types';
import { EditNotificationsStep } from 'components/TicketSteps/NotificationStep/EditNotificationsStep';
import { NotificationBlock } from 'components/TicketSteps/NotificationStep/NotificationBlock';

import { ReactComponent as Stars } from 'assets/icons/stars.svg';
import { regularTextColor, titleTextColor } from 'assets/styles/_palette';

interface NotificationStepProps {
	messages: IMessage[];
	setMessages: (messages: Array<IMessage>) => void;
	description: string;
	ticketTitle: string;
	setTitleError: UseFormSetError<FormTitleValue>;
}

export type PermissionsTypes = 'Email' | 'Push' | 'SMS';

type IPermissions = Record<PermissionsTypes, boolean>;

export interface IFormattedNotification {
	title: string;
	description: string;
	permissions: IPermissions;
	isDeleted?: boolean;
}

export const NotificationStep: FC<NotificationStepProps> = ({
	messages,
	setMessages,
	description,
	ticketTitle,
	setTitleError,
}) => {
	const { t: notificationsTranslate } = useTranslation(NamespacesList['notificationsStep']);
	const { t: commonTranslate, i18n } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();
	const { addEventListener, removeEventListener } = useEventBus();
	const { playText, hasFinishedSpeaking } = useTextToSpeech();
	const { startRecognition } = useSpeechToText();

	const activeTicket = useSelector(getActiveTicket);
	const isRequestLoading = useSelector(getIsNotificationLoading);
	const voiceMode = useSelector(getVoiceMode);
	const editMode = useSelector(getEditMode);
	const choosedFile = useSelector(getChoosedFile);
	const isNavigationButtonAvailable = useSelector(getIsTicketNavigationButtonsAvailable);

	const [notifications, setNotifications] = useState<Array<IFormattedNotification>>([]);

	useEffect(() => {
		addEventListener('messageSend', handleMessageSend);
		addEventListener('generateAccept', generatePotentialNotifications);
		addEventListener('generateDecline', declineNotificationsGenerate);
		addEventListener('regenerateAccept', regenerateNotifications);

		return () => {
			removeEventListener('messageSend', handleMessageSend);
			removeEventListener('generateAccept', generatePotentialNotifications);
			removeEventListener('generateDecline', declineNotificationsGenerate);
			removeEventListener('regenerateAccept', regenerateNotifications);
		};
	}, [messages, voiceMode, hasFinishedSpeaking, choosedFile]);

	useEffect(() => {
		if (!activeTicket?.notifications) {
			return;
		}

		const formattedNotifications = activeTicket.notifications.map(
			(notification: Record<string, any>) => ({
				title: notification.title,
				description: notification.description,
				permissions: {
					Email: notification.email,
					Push: notification.push,
					SMS: notification.sms,
				},
				isDeleted: notification.isDeleted,
			})
		);

		// Dédupliquer par (title + description) pour éviter les doublons à l'affichage
		const seen = new Set<string>();
		const deduped = formattedNotifications.filter((n) => {
			const key = `${n.title ?? ''}|${n.description ?? ''}`;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});

		setNotifications((prevNotifications) => {
			const prevString = JSON.stringify(prevNotifications);
			const newString = JSON.stringify(deduped);

			if (prevString !== newString) {
				return deduped;
			}
			return prevNotifications;
		});
	}, [activeTicket?.notifications, activeTicket?.id]);

	const notificationsStageMessages = useMemo(() => {
		return messages.filter((message) => message.stage === TicketStages.notifications);
	}, [messages]);

	const setSystemMessageState = (state: MessageActionStates) => {
		const generateMessage = messages.find(
			(value) =>
				value.action?.type === MessageActionTypes.generate &&
				value.stage === TicketStages.notifications
		);
		if (generateMessage) {
			const updatedMessage: IMessage = {
				...generateMessage,
				action: {
					type: MessageActionTypes.generate,
					state: state,
				},
			};
			setMessages(
				messages.map((msg) => (msg.sendTime === generateMessage.sendTime ? updatedMessage : msg))
			);
		}
		dispatch(setIsTicketUpdate(true));
	};

	const getUnusedUserMessages = (messageList: Array<IMessage>) => {
		return messageList
			.filter(
				(value) =>
					value.sender === 'user' &&
					value.stage === TicketStages.notifications &&
					value?.action?.type !== MessageActionTypes.used
			)
			.reduce((accumulator, value) => {
				return accumulator + `${value.sender}: ${value.content}\n`;
			}, '');
	};

	// sets all messages state to used
	const getUsedMessages = (messageList: Array<IMessage>) => {
		return messageList.map((message: IMessage) =>
			message.stage === TicketStages.notifications && message.sender === MessageSender.user
				? {
						...message,
						action: {
							...message.action,
							type: MessageActionTypes.used,
							state: MessageActionStates.default,
						},
				  }
				: message
		);
	};

	const getMessagesAfterUpdatedSystemState = (
		messageList: Array<IMessage>,
		type: MessageActionTypes,
		state: MessageActionStates
	) => {
		return messageList.map((message) => {
			const updatedMessage: IMessage = {
				...message,
				action: {
					type: type,
					state: state,
				},
			};

			return message.action?.type === type ? updatedMessage : message;
		});
	};

	const getMessagesString = (messageList: Array<IMessage>): string => {
		return messageList
			.filter((value) => value.stage === TicketStages.notifications)
			.reduce((accumulator, value) => {
				return accumulator + `${value.sender}: ${value.content}\n`;
			}, '');
	};

	const getGenerateMessage = () => {
		const sendTime = new Date();
		sendTime.setTime(new Date().getTime() + 1);

		return {
			content: 'notificationsStep.generateMessage',
			sender: MessageSender.System,
			sendTime: sendTime.toISOString(),
			stage: TicketStages.notifications,
			action: {
				type: MessageActionTypes.generate,
				state: MessageActionStates.default,
			},
			userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
		};
	};

	const getRegenerateMessage = (): IMessage => {
		const sendTime = new Date();
		sendTime.setTime(new Date().getTime() + 1);

		return {
			content: 'notificationsStep.regenerateMessage',
			sender: MessageSender.System,
			sendTime: sendTime.toISOString(),
			stage: TicketStages.notifications,
			action: {
				type: MessageActionTypes.regenerate,
				state: MessageActionStates.default,
			},
			userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
		};
	};

	useEffect(() => {
		if (hasFinishedSpeaking) startRecognition();
	}, [hasFinishedSpeaking]);

	useEffect(() => {
		const hasGenerateMessage = notificationsStageMessages.filter(
			(message) =>
				message?.action?.type === MessageActionTypes.generate &&
				message?.stage === TicketStages.notifications
		).length;

		if (!hasGenerateMessage) {
			const generateMessage: IMessage = getGenerateMessage();

			setMessages([...messages, generateMessage]);
		}
	}, [messages]);

	const regenerateNotifications = async () => {
		try {
			dispatch(setIsNotificationLoading(true));

			getNotifications({
				suggestions: getUnusedUserMessages(messages),
				fileName: choosedFile?.name,
			})
				.then((response) => {
					const requestedNotifications = response.map(
						(notification: any): IFormattedNotification => ({
							title: notification.title,
							description: notification.description,
							permissions: {
								Email: false,
								Push: false,
								SMS: false,
							},
							isDeleted: false,
						})
					);

					setNotifications([...notifications, ...requestedNotifications]);

					setMessages(
						getUsedMessages(
							getMessagesAfterUpdatedSystemState(
								messages,
								MessageActionTypes.regenerate,
								MessageActionStates.accept
							)
						)
					);

					dispatch(setIsTicketUpdate(true));
					dispatch(setIsNotificationLoading(false));
				})
				.catch((err) => {
					if (err.message === notEnoughTokensErrorMessage) {
						const updatedMessages = messages.map((message) =>
							message.stage === TicketStages.notifications &&
							message.action?.type === MessageActionTypes.regenerate
								? { ...message, action: { ...message.action, state: MessageActionStates.default } }
								: message
						);

						setMessages(updatedMessages);

						dispatch(setIsNotificationLoading(false));
						dispatch(setIsTicketUpdate(true));
					}
				});
		} catch (error) {
			dispatch(setIsNotificationLoading(false));
		}
	};

	const generatePotentialNotifications = () => {
		try {
			setSystemMessageState(MessageActionStates.accept);
			dispatch(setIsNotificationLoading(true));

		getPotentialNotifications({ description, fileName: choosedFile?.name })
			.then((response: any) => {
				// Vérifier si la réponse est un tableau ou contient une propriété notifications
				const notificationsArray = Array.isArray(response) ? response : ((response as any)?.notifications || []);
				
				const formattedResponse = notificationsArray.map(
					(notification: any): IFormattedNotification => ({
						title: notification.title || notification.type || 'Notification',
						description: notification.description || notification.message || '',
						permissions: {
							Email: false,
							Push: false,
							SMS: false,
						},
						isDeleted: false,
					})
				);

				const formattedNotifications = formattedResponse.map((notification: IFormattedNotification) => ({
					title: notification.title,
					description: notification.description,
					email: notification.permissions.Email,
					push: notification.permissions.Push,
					sms: notification.permissions.SMS,
					isDeleted: notification.isDeleted,
				}));

				dispatch(
					updateLocalTicket({
						id: activeTicket?.id,
						notifications: formattedNotifications,
					})
				);

				dispatch(setIsNotificationLoading(false));
				dispatch(setIsTicketUpdate(true));
				dispatch(triggerTicketSave());
			})
			.catch((err) => {
				if (err.message === notEnoughTokensErrorMessage) {
					const updatedMessages = messages.map((message) =>
						message.stage === TicketStages.notifications &&
						message.action?.type === MessageActionTypes.generate
							? { ...message, action: { ...message.action, state: MessageActionStates.default } }
							: message
					);

					setMessages(updatedMessages);

					dispatch(setIsNotificationLoading(false));
					dispatch(setIsTicketUpdate(true));
					dispatch(triggerTicketSave());
				} else {
					dispatch(setIsNotificationLoading(false));
				}
			});
	} catch (error) {
		dispatch(setIsNotificationLoading(false));
	}
	};

	const handleMessageSend = (payload: any) => {
		const message: IMessage = { ...payload, stage: TicketStages.notifications };

		// Compter les caractères sans les retours à la ligne
		const contentWithoutNewLines = message?.content.replace(/(\r\n|\n|\r)/gm, '') || '';
		if (contentWithoutNewLines.length <= 4000) {
			const newMessagesList = messages.filter(
				(value) =>
					!(
						value?.action?.type === MessageActionTypes.regenerate &&
						value?.stage === TicketStages.notifications
					)
			);

			const updatedMessagesList = [...newMessagesList, message];
			setMessages(updatedMessagesList);

			dispatch(setIsTyping(true));
			const currentTicket = Object.assign({}, activeTicket);
			try {
				processMessageToChatGpt(message)
					.then((res) => {
						if (currentTicket.id !== activeTicket?.id) {
							setMessages([...newMessagesList, res]);
							dispatch(setIsTicketUpdate(true));
						} else {
							const newMessages = [...newMessagesList, message, res];

							if (voiceMode) playText(res.content);

							const sendTime = new Date();
							sendTime.setTime(new Date().getTime() + 1);

							const regenerateSummaryMessage: IMessage = getRegenerateMessage();

							newMessages.push(regenerateSummaryMessage);

							setMessages(newMessages);

							dispatch(setIsTicketUpdate(true));
						}
						dispatch(setIsTyping(false));
					})
					.catch((err) => {
						if (err.message === notEnoughTokensErrorMessage) {
							cleanUpChatOnError(updatedMessagesList);
						}
					});
			} catch (error) {
				dispatch(setIsTyping(false));
			}
		} else {
			errorNotify(commonTranslate('errors.error'), commonTranslate('errors.messageLengthError'));
		}
	};

	const processMessageToChatGpt = async (message: IMessage) => {
		return getNotificationsConversation({
			chatHistory: getMessagesString(messages),
			userValue: message.content,
			fileName: choosedFile?.name,
		})
			.then((response) => {
				const res: IMessage = {
					content: response,
					sender: MessageSender.chatGPT,
					sendTime: new Date().toISOString(),
					stage: TicketStages.notifications,
					userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
				};
				return res;
			})
			.catch((err) => {
				if (err.message === notEnoughTokensErrorMessage)
					throw new Error(notEnoughTokensErrorMessage);

				return {
					content: '',
					sender: MessageSender.chatGPT,
					sendTime: new Date().toISOString(),
					stage: TicketStages.notifications,
					userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
				};
			});
	};

	const cleanUpChatOnError = (messagesList: Array<IMessage>) => {
		setMessages(messagesList.slice(0, -1));

		dispatch(setIsTyping(false));
	};

	function isPermission(permission: string): permission is PermissionsTypes {
		return ['Email', 'Push', 'SMS'].includes(permission);
	}

	const handlePermissionChange = (type: string, notificationIndex: number) => {
		if (isPermission(type)) {
			const updatedNotification = (
				notification: IFormattedNotification
			): IFormattedNotification => ({
				...notification,
				permissions: {
					...notification.permissions,
					[type]: !notification?.permissions?.[type],
				},
			});

			const updatedNotifications = notifications.map(
				(notification: IFormattedNotification, index) =>
					index === notificationIndex ? updatedNotification(notification) : notification
			);

			setNotifications(updatedNotifications);
		}
	};

	useEffect(() => {
		if (notifications.length === 0) {
			console.log('Notifications array is empty, skipping Redux update');
			return;
		}
		
		const formattedNotifications = notifications.map((notification) => ({
			title: notification.title,
			description: notification.description,
			email: notification.permissions.Email,
			push: notification.permissions.Push,
			sms: notification.permissions.SMS,
			isDeleted: notification.isDeleted,
		}));

		const currentNotifications = activeTicket?.notifications || [];
		
		const hasChanged = 
			currentNotifications.length !== formattedNotifications.length ||
			JSON.stringify(currentNotifications) !== JSON.stringify(formattedNotifications);
		
		if (hasChanged) {
			dispatch(
				updateLocalTicket({
					id: activeTicket?.id,
					notifications: formattedNotifications,
				})
			);
			dispatch(setIsTicketUpdate(true));
			dispatch(triggerTicketSave());
		}
	}, [notifications, activeTicket?.id, dispatch]);

	const declineNotificationsGenerate = () => {
		setSystemMessageState(MessageActionStates.decline);
		dispatch(setIsTicketUpdate(true));
		dispatch(triggerTicketSave());
	};

	const handleNextStepNavigation = () => {
		dispatch(setActiveStep(TicketStages.report));

		if (TicketStages.testCases > (activeTicket?.currentStep || 0)) 		dispatch(triggerTicketSave());
	};

	return (
		<Box
			data-testid={'notifications-step'}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				width: '100%',
				height: '100%',
			}}
		>
			{editMode ? (
				<EditNotificationsStep
					notificationsObj={notifications}
					setNotifications={setNotifications}
					ticketTitle={ticketTitle}
					setTitleError={setTitleError}
				/>
			) : (
				<Box
					className="rtl-able"
					sx={{
						overflow: 'auto',
						height: '100%',
						padding: i18n?.language === 'ar' ? '0 0 0 12px' : '0 12px 0 0',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							width: '90%',
							marginBottom: { md: '12px', xl: '16px' },
						}}
					>
						<Typography className="rtl-able" sx={{ fontWeight: '700', color: titleTextColor }}>
							{notificationsTranslate('generalText.sidePanelTitle')}
						</Typography>
						<Typography className="rtl-able" sx={{ color: regularTextColor }}>
							{notificationsTranslate('generalText.sidePanelDescription')}
						</Typography>
					</Box>

					{isRequestLoading ? (
						<Box
							sx={{
								width: '100%',
								height: 'calc(100% - 112px)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<Oval
								height={24}
								width={24}
								color="#fff"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="#4786ff"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						</Box>
				) : (
					<>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: { md: '16px', xl: '24px' },
								width: '100%',
							}}
						>
							{notifications.map((notification: IFormattedNotification, index: number) => {
								return (
									<NotificationBlock
										key={`notification-${index}`}
										title={notification.title}
										description={notification.description}
										permissions={notification.permissions}
										onPermissionChange={handlePermissionChange}
										index={index}
									/>
								);
							})}
						</Box>
					</>
				)}
				</Box>
			)}

			{notifications.length > 0 && !isRequestLoading && !editMode && isNavigationButtonAvailable ? (
				<Box>
					<Divider sx={{ margin: '16px 0', background: '#E7E8EC' }} />
					<Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
						<Button
							sx={{
								height: '44px',
								display: 'flex',
								gap: '8px',
								textTransform: 'none',
								fontWeight: '600',
							}}
							variant="contained"
							color="primary"
							onClick={handleNextStepNavigation}
						>
							{activeTicket?.businessImpact?.length
								? notificationsTranslate('buttons.goToReport')
								: notificationsTranslate('buttons.generateReport')}
							<Stars style={{ marginLeft: '4px', width: '20px' }} />
						</Button>
					</Box>
				</Box>
			) : null}
		</Box>
	);
};
